// /groups/:groupId/series — the repeating-event list, and /series/:seriesId — one
// series with its upcoming-occurrence preview.
//
// The preview is the point of this screen. In a lazy materialization model an
// organizer otherwise has no way to answer "so when does the next one appear?",
// because most of the dates do not exist as rows yet. Each row therefore shows
// three distinguishable states:
//
//   materialized — a real event exists; link to it.
//   scheduled    — it will be created at the shown trigger time.
//   excluded     — a row existed and was deleted, so this date is a PERMANENT
//                  exception and will never come back. No create button.

import {
  ApiClientError,
  createNextOccurrence,
  createSeries,
  deleteSeries,
  fetchGroup,
  fetchSeries,
  listSeries,
  updateSeries,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { placeRows } from './placeFields.js';
import {
  fmtInstant,
  fmtNaiveDate,
  GroupRole,
  TimeFormat,
  type EventSeries,
  type GroupDetail,
  type SeriesDetail,
  type SeriesPreviewEntry,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { seriesForm } from './seriesForm.js';

// --- The list --------------------------------------------------------------

export const seriesListView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  let series: EventSeries[];
  try {
    [group, series] = await Promise.all([fetchGroup(groupId), listSeries(groupId)]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the repeating events.'));
    return;
  }

  const canManage = group.myRole >= GroupRole.EVENTS_MANAGER;

  const header = el('div', { class: 'page-head' }, [el('h1', {}, 'Repeating events')]);
  if (canManage) {
    header.append(el('a', { class: 'btn', href: `/groups/${groupId}/series/new` }, 'New repeating event'));
  }

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/groups/${groupId}/events` }, `Back to ${group.name} events`)]),
    header,
    el(
      'p',
      { class: 'muted small' },
      'A repeating event is a template. Individual occurrences are created shortly before ' +
        'they happen, so they are not all on the calendar yet.',
    ),
    series.length
      ? el('div', { class: 'event-list' }, series.map((s) => seriesCard(s)))
      : el('p', { class: 'muted' }, 'No repeating events yet.'),
  );
};

function seriesCard(series: EventSeries): HTMLElement {
  const lines: HTMLElement[] = [
    el('h3', { class: 'event-card-title' }, [
      el('a', { href: `/series/${series.seriesId}` }, series.title),
    ]),
    el('p', { class: 'muted' }, series.label),
    el(
      'p',
      { class: 'muted small' },
      `Starts ${fmtNaiveDate(series.startDate)}${
        series.endDate ? ` · until ${fmtNaiveDate(series.endDate)}` : ' · no end date'
      }`,
    ),
  ];

  if (series.feedPostTime) {
    lines.push(
      el(
        'p',
        { class: 'small' },
        `Each occurrence appears ${series.feedPostDaysBefore} day${
          series.feedPostDaysBefore === 1 ? '' : 's'
        } ahead at ${series.feedPostTime.slice(0, 5)}`,
      ),
    );
  } else {
    // Worth surfacing on the card: a series with no post time looks identical to
    // a scheduled one but silently generates nothing.
    lines.push(el('p', { class: 'notice' }, 'Manual only: occurrences never appear on their own.'));
  }

  lines.push(
    el(
      'p',
      { class: 'muted small' },
      `${series.occurrenceCount} occurrence${series.occurrenceCount === 1 ? '' : 's'} created so far`,
    ),
  );

  return el('article', { class: 'card event-card' }, lines);
}

// --- Create ----------------------------------------------------------------

export const seriesNewView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let group: GroupDetail;
  try {
    group = await fetchGroup(groupId);
  } catch {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load this group.'));
    return;
  }

  if (group.myRole < GroupRole.EVENTS_MANAGER) {
    main.replaceChildren(
      el('p', { class: 'form-error' }, 'You need to be an Events Manager to create repeating events.'),
    );
    return;
  }

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/groups/${groupId}/series` }, 'Back to repeating events')]),
    el('h1', {}, `New repeating event in ${group.name}`),
    el(
      'p',
      { class: 'notice' },
      'This creates the template only. No events are created and nobody is notified until an ' +
        'occurrence actually appears.',
    ),
    el('section', { class: 'card' }, [
      seriesForm({
        groupTimeZone: group.timeZone,
        submitLabel: 'Create repeating event',
        action: `/groups/${groupId}/series`,
        onSubmit: async (body) => {
          const created = await createSeries(groupId, body);
          navigate(`/series/${created.seriesId}`, true);
        },
      }),
    ]),
  );
};

// --- Detail ----------------------------------------------------------------

export const seriesDetailView: View = async (outlet, ctx) => {
  const seriesId = Number(ctx.params.seriesId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(seriesId) || seriesId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid series.'));
    return;
  }

  let series: SeriesDetail;
  let zone: string;
  try {
    [series, zone] = await Promise.all([fetchSeries(seriesId), viewerZone()]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.status === 404
          ? 'That repeating event does not exist.'
          : 'Could not load it.',
      ),
    );
    return;
  }

  const canManage = series.myRole >= GroupRole.EVENTS_MANAGER;

  const sections: HTMLElement[] = [
    el('p', {}, [el('a', { href: `/groups/${series.groupId}/series` }, 'Back to repeating events')]),
    el('h1', {}, series.title),
    detailsCard(series),
    previewCard(series, zone, canManage),
  ];

  if (canManage) sections.push(editCard(series), dangerCard(series));

  main.replaceChildren(...sections);
};

function detailsCard(series: EventSeries): HTMLElement {
  const rows: HTMLElement[] = [
    field('Repeats', series.label),
    field('First occurrence', fmtNaiveDate(series.startDate)),
    field('Repeat until', series.endDate ? fmtNaiveDate(series.endDate) : 'No end date'),
  ];

  if (series.startTime) {
    rows.push(
      field(
        'Time',
        `${series.startTime.slice(0, 5)}${series.endTime ? ` to ${series.endTime.slice(0, 5)}` : ''} (${series.timeZone})`,
      ),
    );
  } else {
    rows.push(field('Time', `All day (${series.timeZone})`));
  }

  rows.push(...placeRows(series.location, series, field));
  if (series.description) rows.push(field('Details', series.description));
  if (series.rsvpMin !== null) rows.push(field('Minimum RSVPs', String(series.rsvpMin)));
  if (series.rsvpMax !== null) rows.push(field('Maximum RSVPs', String(series.rsvpMax)));

  rows.push(
    field(
      'Occurrences appear',
      series.feedPostTime
        ? `${series.feedPostDaysBefore} day${series.feedPostDaysBefore === 1 ? '' : 's'} ahead at ${series.feedPostTime.slice(0, 5)} (${series.timeZone})`
        : 'Only when you press “Create next occurrence”',
    ),
  );

  return el('section', { class: 'card' }, rows);
}

function field(label: string, value: string | (string | HTMLElement)[]): HTMLElement {
  return el('p', {}, [el('strong', {}, `${label}: `), ...(Array.isArray(value) ? value : [value])]);
}

/**
 * The upcoming dates.
 *
 * `trigger` is shown as the series' own naive wall-clock, with the zone named,
 * rather than converted into the viewer's zone: it is a scheduling setting the
 * organizer typed, and showing it back shifted would make it look changed.
 * `triggerUtc` is the real instant and is what "already passed" is judged on.
 */
function previewCard(series: SeriesDetail, zone: string, canManage: boolean): HTMLElement {
  if (series.preview.length === 0) {
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Upcoming occurrences'),
      el(
        'p',
        { class: 'muted' },
        'The rule produces no dates in the next year. Check the repeat settings and the end date.',
      ),
    ]);
  }

  const rows = series.preview.map((p) => previewRow(p, zone));

  const children: HTMLElement[] = [
    el('h2', {}, 'Upcoming occurrences'),
    el(
      'p',
      { class: 'muted small' },
      'Computed from the rule. Dates without a link do not exist as events yet.',
    ),
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'table' }, [
        el('thead', {}, [
          el('tr', {}, ['Date', 'Status', 'Appears'].map((h) => el('th', {}, h))),
        ]),
        el('tbody', {}, rows),
      ]),
    ]),
  ];

  if (canManage) children.push(materializeButton(series));
  return el('section', { class: 'card' }, children);
}

function previewRow(p: SeriesPreviewEntry, zone: string): HTMLElement {
  const status = p.materialized
    ? el('a', { href: `/events/${p.eventId}` }, 'Created. Open it')
    : p.excluded
      ? // A deleted occurrence is a permanent exception. Saying so is the whole
        // reason this state is reported separately: the PHP preview showed it as
        // "not created" beside a button that silently did nothing.
        el('span', { class: 'muted' }, 'Deleted, will not come back')
      : el('span', {}, 'Not created yet');

  const appears = p.trigger
    ? p.triggerPassed && !p.materialized
      ? // Due but absent: the cron has not run since. Worth naming, because
        // otherwise it reads as a bug.
        el('span', { class: 'muted' }, `Due ${p.trigger}, waiting for the next run`)
      : el('span', {}, p.triggerUtc ? fmtInstant(p.triggerUtc, zone, TimeFormat.INSTANT) : p.trigger)
    : el('span', { class: 'muted' }, 'Manual only');

  return el('tr', { class: p.excluded ? 'row-excluded' : undefined }, [
    el('td', {}, fmtNaiveDate(p.date)),
    el('td', {}, status),
    el('td', {}, appears),
  ]);
}

function materializeButton(series: SeriesDetail): HTMLElement {
  const status = el('p', { class: 'muted small' });
  const btn = el('button', { class: 'btn btn-standalone', type: 'button' }, 'Create next occurrence');

  btn.addEventListener('click', async () => {
    if (
      !window.confirm(
        'Create the next occurrence now and notify members? This skips the normal schedule.',
      )
    ) {
      return;
    }
    btn.setAttribute('disabled', 'disabled');
    status.textContent = 'Creating';
    try {
      const result = await createNextOccurrence(series.seriesId);
      navigate(`/events/${result.eventId}`, true);
    } catch (err) {
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not create the next occurrence.';
      btn.removeAttribute('disabled');
    }
  });

  return el('div', {}, [
    btn,
    el(
      'p',
      { class: 'muted small' },
      'Creates it immediately and announces it, ahead of its normal schedule.',
    ),
    status,
  ]);
}

function editCard(series: SeriesDetail): HTMLElement {
  return el('section', { class: 'card' }, [
    el('h2', {}, 'Edit the template'),
    el(
      'p',
      { class: 'notice' },
      'Changes apply to occurrences created from now on. Occurrences already on the calendar are ' +
        'left alone, because they carry RSVPs.',
    ),
    seriesForm({
      series,
      groupTimeZone: null,
      submitLabel: 'Save template',
      action: `/series/${series.seriesId}`,
      onSubmit: async (body) => {
        await updateSeries(series.seriesId, body);
        navigate(`/series/${series.seriesId}`, true);
      },
    }),
  ]);
}

function dangerCard(series: SeriesDetail): HTMLElement {
  const alsoOccurrences = el('input', { id: 'sr-del-events', type: 'checkbox' }) as HTMLInputElement;
  const btn = el('button', { class: 'btn btn-danger btn-standalone', type: 'button' }, 'Stop this repeating event');

  btn.addEventListener('click', async () => {
    const extra = alsoOccurrences.checked
      ? ' Upcoming occurrences will also be removed.'
      : ' Occurrences already created will stay.';
    if (!window.confirm(`Stop “${series.title}”?${extra}`)) return;

    btn.setAttribute('disabled', 'disabled');
    try {
      await deleteSeries(series.seriesId, alsoOccurrences.checked);
      navigate(`/groups/${series.groupId}/series`, true);
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not stop it.');
      btn.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Stop repeating'),
    el(
      'p',
      { class: 'muted' },
      'No more occurrences will be created. Past occurrences and every RSVP are always kept.',
    ),
    el('label', { class: 'check', for: 'sr-del-events' }, [
      alsoOccurrences,
      ' Also remove upcoming occurrences that already exist',
    ]),
    btn,
  ]);
}
